import React, { Component } from 'react';
import { liveDiffUnsupportedReason, liveDiffCaveat } from '../../../util/engine-support';
import Drawer from '@material-ui/core/Drawer';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MuiButton from '@material-ui/core/Button';
import Button from '../../../components/button';
import Preloader from '../../../components/preloader';
import DDItemCard from './dd-item-card';
import ChangeRequestDetailSheet from '../../../components/change-request-detail-sheet';
import ImportMissingDialog from './import-missing-dialog';
import OwnershipPanel from './OwnershipPanel';
import ClassificationPanel from './ClassificationPanel';
import ObjectVisibilityTable from './ObjectVisibilityTable';
import WhoCanSeePanel from './WhoCanSeePanel';
import Auth from '../../../auth/auth';

export const getDisplayObjectsFromSchemas = (schemas = []) => {
  return schemas.reduce((acc, schema) => {
    return acc.concat(
      schema.tables || [],
      schema.views || [],
      schema.storedProcedures || [],
      schema.functions || []
    );
  }, []);
};

const SUMMARY_EXCLUDED_KEYS = new Set([
  'totalSchemas',
  'totalRelationships',
  'totalIndexes',
  'totalTags'
]);

export const getVisibleSummaryKeys = (summary = {}) => {
  return Object.keys(summary).filter((key) => !SUMMARY_EXCLUDED_KEYS.has(key));
};

export const getComputedTotalObjects = (schemas = []) => {
  return schemas.reduce((acc, schema) => (
    acc +
    (schema.tables || []).length +
    (schema.views || []).length +
    (schema.storedProcedures || []).length
  ), 0);
};

// Total pending change *requests* on a category's objects. Counts requests, not
// distinct objects, so the per-category badge reconciles with the header count
// (which is per-request after the #576 one-request-per-column split).
export const countCategoryPendingChanges = (objects = [], pendingChangesByObject = {}) => {
  return objects.reduce((sum, obj) => {
    const objName = obj.tableName || obj.name || obj.objectName;
    return sum + (pendingChangesByObject[objName] ? pendingChangesByObject[objName].length : 0);
  }, 0);
};

// Documentation status is scored over business-context *fields* (#619), not objects:
// each object has one context field (its table/view/proc-level context) plus one per
// column. Returns { total, filled } so the % reflects how many context fields are
// actually written — objects themselves are not counted as separate units.
export const countBusinessContextFields = (objects = []) => {
  const hasContext = (v) => !!(v && String(v).trim());
  let total = 0;
  let filled = 0;
  objects.forEach((obj) => {
    total += 1;
    if (hasContext(obj.businessContext)) filled += 1;
    (obj.columns || []).forEach((col) => {
      total += 1;
      if (hasContext(col.businessContext)) filled += 1;
    });
  });
  return { total, filled };
};

class DDDetailsView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drawerOpen: false,
      selectedItem: null,
      selectedItemType: null,
      selectedItemObjectId: null,
      editedBusinessContext: '',
      ownerValue: [],
      techOwnerValue: [],
      userListLoaded: false,
      techOwnerUserListLoaded: false,
      expandedColumnsInPanel: {},
      expandedColumnsInCards: {},
      expandedCategories: {},
      // Editable fields
      editedName: '',
      editedDescription: '',
      hasUnsavedChanges: false,
      searchQuery: '',
      filterOwnedByMe: false,
      // Workflow / change request dialog
      changeDialogOpen: false,
      selectedChangeRequest: null,
      filteredPendingChanges: [], // Filtered changes for current context (category or all)
      // Rollback confirmation dialog
      rollbackDialogOpen: false,
      rollbackTargetRequest: null,
      rollbackReason: '',
      rollbackReasonError: false,
      // Import Missing dialog
      importMissingDialogOpen: false,
      pendingImportPayload: null,
    };
  }

  componentDidMount() {
    this.initializeEditableFields(true);
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.dictionaryDetails?.instance?.instanceId
      || prevProps.dictionaryDetails?.instance?.instance_id;
    const currId = this.props.dictionaryDetails?.instance?.instanceId
      || this.props.dictionaryDetails?.instance?.instance_id;

    if (prevId !== currId) {
      this.initializeEditableFields(true);
    } else if (this.props.dictionaryDetails !== prevProps.dictionaryDetails) {
      this.initializeEditableFields(false);
    }

    // Close the Import Missing dialog once importing completes (success or failure)
    if (prevProps.isImportingMissing && !this.props.isImportingMissing) {
      this.setState({ importMissingDialogOpen: false });
    }
  }

  initializeEditableFields = (resetUnsaved = false) => {
    const { dictionaryDetails } = this.props;
    if (dictionaryDetails && dictionaryDetails.instance) {
      this.setState(prevState => ({
        editedName: dictionaryDetails.instance.instanceName || '',
        editedDescription: dictionaryDetails.instance.description || '',
        hasUnsavedChanges: resetUnsaved ? false : prevState.hasUnsavedChanges
      }));
    }
  }

  handleNameChange = (event) => {
    this.setState({ 
      editedName: event.target.value,
      hasUnsavedChanges: true 
    });
  }

  handleDescriptionChange = (event) => {
    this.setState({ 
      editedDescription: event.target.value,
      hasUnsavedChanges: true 
    });
  }

  handlePanelChange = (panel) => {
    this.setState({
      expandedCategories: {
        ...this.state.expandedCategories,
        [panel]: !this.state.expandedCategories[panel]
      }
    });
  }

  toggleCategory = (category) => {
    this.setState(prevState => ({
      expandedCategories: {
        ...prevState.expandedCategories,
        [category]: !prevState.expandedCategories[category]
      }
    }));
  }

  toggleColumnInPanel = (columnId) => {
    this.setState(prevState => {
      const newExpanded = { ...prevState.expandedColumnsInPanel };
      if (newExpanded[columnId]) {
        delete newExpanded[columnId];
      } else {
        newExpanded[columnId] = true;
      }
      return { expandedColumnsInPanel: newExpanded };
    });
  }

  toggleColumnsInCard = (itemId) => {
    this.setState(prevState => ({
      expandedColumnsInCards: {
        ...prevState.expandedColumnsInCards,
        [itemId]: !prevState.expandedColumnsInCards[itemId]
      }
    }));
  }

  getCategoryLabel = (type) => {
    const labels = {
      'BASE TABLE': 'Tables',
      'VIEW': 'Views',
      'STORED_PROCEDURE': 'Stored Procedures',
      'PROCEDURE': 'Stored Procedures',
      'FUNCTION': 'Functions',
      'TRIGGER': 'Triggers'
    };
    return labels[type] || this.getTypeLabel(type);
  }

  getStatusIcon = (item) => {
    const hasContext = item.businessContext && item.businessContext.trim();
    return (
      <span 
        className={`material-icons status-icon ${hasContext ? 'completed' : 'incomplete'}`}
      >
        {hasContext ? 'check_circle' : 'error_outline'}
      </span>
    );
  }

  openDrawer = (item, itemType) => {
    // Load existing owner values if present
    const existingBusinessOwner = item.businessOwner || [];
    const existingTechOwner = item.techOwner || [];
    const ownerValue = this.getOwnerOptionsFromData(existingBusinessOwner);
    const techOwnerValue = this.getTechOwnerOptionsFromData(existingTechOwner);

    this.setState({
      drawerOpen: true,
      selectedItem: item,
      selectedItemType: itemType,
      selectedItemObjectId: this.objIdOf(item),
      editedBusinessContext: item.businessContext || '',
      ownerValue: ownerValue,
      techOwnerValue: techOwnerValue
    });
  }

  /**
   * S4's per-row "Classify" — opens the SAME drawer a card click opens, where ClassificationPanel
   * already lives. No second dialog: two ways to propose a reclassification would be two places
   * for the rule to drift.
   *
   * The sight row carries only ids, so the real catalogue object is looked up from the props the
   * page already has — passing the sight row straight through would give the drawer an object with
   * no columns, no business context and no owners.
   */
  openDrawerForObject = (row) => {
    const all = getDisplayObjectsFromSchemas((this.props.dictionaryDetails || {}).schemas || []);
    const match = all.find((o) => this.objIdOf(o) === row.objectId);
    if (match) this.openDrawer(match, match.objectType || 'BASE TABLE');
  }

  closeDrawer = () => {
    const { selectedItem, editedBusinessContext, ownerValue, techOwnerValue, selectedItemType, selectedItemObjectId } = this.state;

    // Only persist / mark dirty when an editable field genuinely changed. Opening a
    // card just to read it (and closing) must not create a phantom change (#635).
    let changed = false;
    if (selectedItem) {
      const contextChanged = (editedBusinessContext || '') !== (selectedItem.businessContext || '');
      const bizChanged = this.ownerListChanged(selectedItem.businessOwner, ownerValue);
      const techChanged = this.ownerListChanged(selectedItem.techOwner, techOwnerValue);
      changed = contextChanged || bizChanged || techChanged;

      if (changed) {
        const toOwnerObj = (o) => {
          const emailMatch = o.label.match(/\(([^)]+)\)/);
          const email = emailMatch ? emailMatch[1] : '';
          const name = o.label.replace(/\s*\([^)]+\)\s*$/, '').trim();
          return { userId: o.value, name, email };
        };

        // Overwrite only the field that actually changed. Round-tripping an
        // unchanged, legacy-shaped owner array through toOwnerObj would trip the
        // parent's JSON owner-diff and emit a phantom "Changed owner" (#635/#611).
        const updatedItem = {
          ...selectedItem,
          businessContext: contextChanged ? editedBusinessContext : selectedItem.businessContext,
          businessOwner: bizChanged ? ownerValue.map(toOwnerObj) : selectedItem.businessOwner,
          techOwner: techChanged ? techOwnerValue.map(toOwnerObj) : selectedItem.techOwner,
        };

        if (this.props.onUpdateItem && selectedItemObjectId) {
          this.props.onUpdateItem(selectedItemType, selectedItemObjectId, updatedItem);
        } else if (!selectedItemObjectId) {
          // ponytail: id-less object never happens under deterministic dd_* UUIDs;
          // skip the write rather than fall back to a positional index (the bug).
          console.warn('closeDrawer: selected object has no id; skipping owner/context flush');
        }
      }
    }

    this.setState((prev) => ({
      drawerOpen: false,
      selectedItem: null,
      selectedItemType: null,
      selectedItemObjectId: null,
      editedBusinessContext: '',
      ownerValue: [],
      techOwnerValue: [],
      hasUnsavedChanges: changed ? true : prev.hasUnsavedChanges,
    }));
  }

  handleBusinessContextChange = (event) => {
    this.setState({ 
      editedBusinessContext: event.target.value,
      hasUnsavedChanges: true
    });
  }

  handleOwnerChange = (event, newValue) => {
    this.setState({ 
      ownerValue: newValue,
      hasUnsavedChanges: true
    });
  }

  handleTechOwnerChange = (event, newValue) => {
    this.setState({ 
      techOwnerValue: newValue,
      hasUnsavedChanges: true
    });
  }

  handleOwnerDropdownOpen = () => {
    if (!this.state.userListLoaded && this.props.fetchUserList) {
      this.props.fetchUserList();
      this.setState({ userListLoaded: true });
    }
  }

  handleTechOwnerDropdownOpen = () => {
    if (!this.state.techOwnerUserListLoaded && this.props.fetchTechOwnerList) {
      this.props.fetchTechOwnerList();
      this.setState({ techOwnerUserListLoaded: true });
    }
  }

  // Normalize an owner/user row into an Autocomplete option. Guards null rows
  // (a single bad userList row otherwise crashes the panel during render) and
  // coerces the id to a string so a stored owner id — JSON, sometimes a string
  // like "10" — matches an option id from the users API, which is numeric.
  // This id-type mismatch is what made owners un-reassignable after removal (#611).
  toOwnerOption = (o) => {
    if (!o || typeof o !== 'object') return null;
    const id = o.userId != null ? o.userId : o.id;
    if (id == null) return null; // an id-less owner is unusable (and would collide on '')
    return {
      label: `${o.name || ''}${o.email ? ` (${o.email})` : ''}`.trim(),
      value: String(id)
    };
  }

  getOwnerOptions = () => {
    const { userList } = this.props;
    if (!Array.isArray(userList)) return [];
    return userList.map(this.toOwnerOption).filter(Boolean);
  }

  getOwnerOptionsFromData = (ownerData) => {
    if (!Array.isArray(ownerData)) return [];
    return ownerData.map(this.toOwnerOption).filter(Boolean);
  }

  getTechOwnerOptions = () => {
    const { techOwnerList } = this.props;
    if (!Array.isArray(techOwnerList)) return [];
    return techOwnerList.map(this.toOwnerOption).filter(Boolean);
  }

  getTechOwnerOptionsFromData = (ownerData) => {
    if (!Array.isArray(ownerData)) return [];
    return ownerData.map(this.toOwnerOption).filter(Boolean);
  }

  // Stable object identity (deterministic dd_* UUID). Objects are edited by id,
  // not by list position — a filtered/sorted/heterogeneous grid otherwise resolves
  // to the wrong object on save (#635). Snake_case tolerated: the details API mixes both.
  objIdOf = (o) => (o && (o.objectId || o.object_id)) || null;

  // True only when the owner set genuinely differs. Both sides are normalized
  // through toOwnerOption (String(id), tolerates legacy numeric userId and o.id,
  // drops id-less rows) so a legacy-shaped stored owner never reads as a phantom
  // change on an unrelated edit (#611 id-shape, #635 spurious "unsaved changes").
  ownerListChanged = (storedOwners, optionValue) => {
    const stored = this.getOwnerOptionsFromData(storedOwners).map((o) => o.value).sort();
    const current = (optionValue || []).map((o) => String(o.value)).sort();
    return JSON.stringify(stored) !== JSON.stringify(current);
  }

  handleSaveAll = () => {
    const { dictionaryDetails, onSave } = this.props;
    const {
      editedName, editedDescription,
      selectedItem, selectedItemType, selectedItemObjectId,
      editedBusinessContext, ownerValue, techOwnerValue, pendingImportPayload
    } = this.state;

    if (!dictionaryDetails || !dictionaryDetails.instance) return;

    // Deep clone to avoid mutating props
    const updatedDetails = JSON.parse(JSON.stringify(dictionaryDetails));

    // 1. Instance-level changes
    updatedDetails.instance.instanceName = editedName;
    updatedDetails.instance.description = editedDescription;

    // 2. If side panel is open, flush all its edits into the matching object in schemas
    if (selectedItem && selectedItemType !== null && selectedItemObjectId) {
      const schemaTypeMap = {
        'BASE TABLE': 'tables',
        'TABLE': 'tables',
        'VIEW': 'views',
        'STORED_PROCEDURE': 'storedProcedures',
        'PROCEDURE': 'storedProcedures',
        'FUNCTION': 'functions',
        'TRIGGER': 'triggers',
      };
      const schemaKey = schemaTypeMap[selectedItemType] || null;

      if (schemaKey && updatedDetails.schemas) {
        const toOwnerObj = (o) => {
          const emailMatch = o.label.match(/\(([^)]+)\)/);
          const email = emailMatch ? emailMatch[1] : '';
          const name = o.label.replace(/\s*\([^)]+\)\s*$/, '').trim();
          return { userId: o.value, name, email };
        };

        // Only overwrite a field that actually changed — see closeDrawer (#635).
        const contextChanged = (editedBusinessContext || '') !== (selectedItem.businessContext || '');
        const bizChanged = this.ownerListChanged(selectedItem.businessOwner, ownerValue);
        const techChanged = this.ownerListChanged(selectedItem.techOwner, techOwnerValue);

        // Resolve the object by stable id, not by position — a filtered grid maps a
        // filtered index onto the wrong object in this unfiltered list (#635).
        let found = false;
        updatedDetails.schemas.forEach(schema => {
          if (found) return;
          (schema[schemaKey] || []).forEach((obj) => {
            if (found) return;
            if (this.objIdOf(obj) === selectedItemObjectId) {
              if (contextChanged) obj.businessContext = editedBusinessContext;
              if (bizChanged) obj.businessOwner = ownerValue.map(toOwnerObj);
              if (techChanged) obj.techOwner = techOwnerValue.map(toOwnerObj);
              if (selectedItem.columns) {
                obj.columns = selectedItem.columns.map((col) => ({ ...col }));
              }
              found = true;
            }
          });
        });
      }
    }

    this.setState({
      hasUnsavedChanges: false,
      drawerOpen: false,
      selectedItem: null,
      selectedItemType: null,
      selectedItemObjectId: null,
      editedBusinessContext: '',
      ownerValue: [],
      techOwnerValue: [],
      pendingImportPayload: null,
    });

    if (onSave) {
      onSave(updatedDetails, pendingImportPayload || null);
    }
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  }

  handleSearchChange = (event) => {
    this.setState({ searchQuery: event.target.value });
  }

  handlePiiToggle = (col, index) => {
    const newIsPii = !col.isPii;
    const updatedColumns = [...this.state.selectedItem.columns];
    updatedColumns[index] = { ...col, isPii: newIsPii };
    this.setState({
      selectedItem: { ...this.state.selectedItem, columns: updatedColumns },
      hasUnsavedChanges: true
    });
    if (col.columnId && this.props.onUpdateColumnIsPii) {
      this.props.onUpdateColumnIsPii(col.columnId, newIsPii);
    }
  }

  handleAiEligibleToggle = (col, index) => {
    const newIsAiEligible = !col.isAiEligible;
    const updatedColumns = [...this.state.selectedItem.columns];
    updatedColumns[index] = { ...col, isAiEligible: newIsAiEligible };
    this.setState({
      selectedItem: { ...this.state.selectedItem, columns: updatedColumns },
      hasUnsavedChanges: true
    });
    if (col.columnId && this.props.onUpdateColumnIsAiEligible) {
      this.props.onUpdateColumnIsAiEligible(col.columnId, newIsAiEligible);
    }
  }

  handleFilterOwnedByMe = () => {
    this.setState({ filterOwnedByMe: !this.state.filterOwnedByMe });
  }

  isUserAdmin = () => {
    try {
      const auth = new Auth();
      return auth.getRoleId() === 2;
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  }

  isUserTechnical = () => {
    try {
      const auth = new Auth();
      return auth.getRoleId() === 1;
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  }

  canEditItem = (item) => {
    try {
      const auth = new Auth();
      const roleId = auth.getRoleId();

      // Admin (data_custodian) can edit everything
      if (roleId === 2) {
        return true;
      }

      // #623: owners (roleId 3) can edit items they own — a Business OR Tech Owner match.
      // Same rule as the "Owned by Me" filter, so delegate to the single ownership check.
      if (roleId === 3) {
        return this.isOwnedByMe(item);
      }

      // All other roles cannot edit
      return false;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  }

  isOwnedByMe = (item) => {
    try {
      const auth = new Auth();
      const userEmail = auth.getUserEmail();

      if (!userEmail || !item) {
        return false;
      }

      // #623: ownership is a Business Owner OR Tech Owner email match (case-insensitive).
      // Per-row guards so a malformed entry in one list can't throw or veto a valid match
      // in the other.
      const email = userEmail.toLowerCase();
      const ownsVia = (owners) => Array.isArray(owners)
        && owners.some((o) => o && o.email && o.email.toLowerCase() === email);
      return ownsVia(item.businessOwner) || ownsVia(item.techOwner);
    } catch (error) {
      console.error('Error checking ownership:', error);
      return false;
    }
  }

  getTypeIcon = (type) => {
    const typeUpper = (type || '').toUpperCase();
    if (typeUpper.includes('TABLE')) return 'table_chart';
    if (typeUpper.includes('VIEW')) return 'view_list';
    if (typeUpper.includes('PROCEDURE') || typeUpper.includes('FUNCTION')) return 'code';
    if (typeUpper.includes('TRIGGER')) return 'flash_on';
    return 'description';
  }

  getTypeLabel = (type) => {
    // Convert type to readable label (e.g., 'BASE TABLE' -> 'Tables', 'VIEW' -> 'Views')
    const typeUpper = (type || '').toUpperCase();
    if (typeUpper === 'BASE TABLE') return 'Tables';
    if (typeUpper === 'VIEW') return 'Views';
    if (typeUpper === 'STORED_PROCEDURE' || typeUpper === 'PROCEDURE') return 'Stored Procedures';
    if (typeUpper === 'FUNCTION') return 'Functions';
    if (typeUpper === 'TRIGGER') return 'Triggers';
    // For other types, just capitalize and pluralize
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() + 's';
  }

  getTypePanelKey = (type) => {
    // Convert type to a panel key (e.g., 'BASE TABLE' -> 'baseTable')
    return type.replace(/\s+/g, '').toLowerCase();
  }

  renderItemCard = (item, itemType, index) => {
    const isSelected = this.state.selectedItem?.objectId === item.objectId;
    const isColumnsExpanded = this.state.expandedColumnsInCards[item.objectId];
    return (
      <div key={item.objectId || index} style={{ position: 'relative' }}>
        <DDItemCard
          item={item}
          isSelected={isSelected}
          onClick={() => this.openDrawer(item, itemType)}
          columns={item.columns || []}
          businessContextData={null}
          isColumnsExpanded={isColumnsExpanded}
          onToggleColumns={() => this.toggleColumnsInCard(item.objectId)}
          categoryLabel={this.getCategoryLabel(item.tableType || item.objectType || itemType)}
        />
      </div>
    );
  }

  renderPendingTile = (obj, categoryLabel) => {
    const name = obj.objectName || obj.name || obj.tableName || obj.viewName || obj.procedureName;
    return (
      <div key={`pending-${name}`} className="schema-item context-card pending-import-card">
        <div className="context-card-header">
          <span className="material-icons pending-import-status-icon">schedule</span>
          <div className="card-info">
            <div className="card-name">{name}</div>
            <div className="card-type">{categoryLabel}</div>
          </div>
          <span className="pending-import-chip">
            <span className="material-icons">pending</span>
            Pending
          </span>
        </div>
        <div className="pending-import-card-footer">
          Will be imported on Save Changes
        </div>
      </div>
    );
  }

  _getPendingChangesByObject = () => {
    const { pendingChanges } = this.props;
    const map = {};
    (pendingChanges || []).forEach((cr) => {
      if (cr.object_name) {
        if (!map[cr.object_name]) map[cr.object_name] = [];
        map[cr.object_name].push(cr);
      }
    });
    return map;
  }

  openRollbackDialog = (changeRequest) => {
    this.setState({ rollbackDialogOpen: true, rollbackTargetRequest: changeRequest, rollbackReason: '', rollbackReasonError: false });
  }

  closeRollbackDialog = () => {
    this.setState({ rollbackDialogOpen: false, rollbackTargetRequest: null, rollbackReason: '', rollbackReasonError: false });
  }

  confirmRollback = () => {
    const { rollbackTargetRequest, rollbackReason } = this.state;
    if (!rollbackReason || !rollbackReason.trim()) {
      this.setState({ rollbackReasonError: true });
      return;
    }
    if (this.props.onRollbackChangeRequest && rollbackTargetRequest) {
      this.props.onRollbackChangeRequest(rollbackTargetRequest.request_id, rollbackReason.trim());
    }
    this.closeRollbackDialog();
  }

  canRollback = (status) => {
    return ['APPROVED', 'REJECTED'].includes(status);
  }

  getStatusChipStyle = (status) => {
    const styles = {
      APPROVED: { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
      REJECTED: { backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' },
      PENDING: { backgroundColor: '#fff8e1', color: '#f57c00', border: '1px solid #ffe082' },
      ROLLED_BACK: { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
    };
    return styles[status] || { backgroundColor: '#f5f5f5', color: '#333' };
  }

  renderRollbackConfirmDialog = () => {
    const { rollbackDialogOpen, rollbackTargetRequest, rollbackReason, rollbackReasonError } = this.state;
    const isRollingBack = (this.props.isLoading || []).includes('rollback');

    return (
      <Dialog open={rollbackDialogOpen} onClose={this.closeRollbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="material-icons" style={{ color: '#e65100' }}>undo</span>
            <span>Confirm Rollback</span>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {rollbackTargetRequest && (
            <>
              <div style={{
                backgroundColor: '#fff3e0',
                border: '1px solid #ffcc80',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <span className="material-icons" style={{ color: '#e65100', fontSize: 20, marginTop: 2 }}>warning</span>
                <div>
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#e65100' }}>
                    Warning: This action cannot be undone automatically.
                  </Typography>
                  <Typography variant="body2" style={{ color: '#555', marginTop: 4 }}>
                    This will revert the status of the change request to <strong>ROLLED_BACK</strong>
                    {rollbackTargetRequest.status === 'APPROVED' ? ' and restore the entity to its previous state.' : '.'}
                  </Typography>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Typography variant="subtitle2" color="textSecondary">Change Request</Typography>
                <Typography variant="body1">
                  <strong>{rollbackTargetRequest.object_name || rollbackTargetRequest.request_id}</strong>
                  {rollbackTargetRequest.column_name && ` → ${rollbackTargetRequest.column_name}`}
                </Typography>
              </div>

              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Current Status</Typography>
                  <Chip
                    label={rollbackTargetRequest.status}
                    size="small"
                    style={this.getStatusChipStyle(rollbackTargetRequest.status)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="material-icons" style={{ color: '#9e9e9e' }}>arrow_forward</span>
                </div>
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">New Status</Typography>
                  <Chip
                    label="ROLLED_BACK"
                    size="small"
                    style={this.getStatusChipStyle('ROLLED_BACK')}
                  />
                </div>
              </div>
            </>
          )}

          <TextField
            label="Reason for Rollback *"
            value={rollbackReason}
            onChange={(e) => this.setState({ rollbackReason: e.target.value, rollbackReasonError: false })}
            fullWidth
            multiline
            rows={3}
            placeholder="Explain why this action is being rolled back..."
            error={rollbackReasonError}
            helperText={rollbackReasonError ? 'A reason is required to perform a rollback.' : ''}
            variant="outlined"
            inputProps={{'data-testid': 'dd-rollback-reason'}}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={this.closeRollbackDialog} disabled={isRollingBack} data-testid="dd-rollback-cancel">Cancel</MuiButton>
          <MuiButton
            onClick={this.confirmRollback}
            color="secondary"
            variant="contained"
            disabled={isRollingBack}
            style={{ backgroundColor: '#e65100', color: '#fff' }}
            data-testid="dd-rollback-confirm"
          >
            {isRollingBack ? 'Rolling Back...' : 'Confirm Rollback'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const {
      dictionaryDetails, isLoading, onSave, pendingChanges, isRefreshing, onRefreshSchema, onWorkflowAction,
      missingItems, isLoadingMissing, isImportingMissing, onLoadMissingObjects,
    } = this.props;
    const { drawerOpen, selectedItem, editedBusinessContext, changeDialogOpen, selectedChangeRequest, importMissingDialogOpen, pendingImportPayload } = this.state;

    if (isLoading) {
      return <Preloader loading={true} />;
    }

    if (!dictionaryDetails || !dictionaryDetails.instance) {
      return (
        <div className="dd-details-view">
          <p>No dictionary details available.</p>
        </div>
      );
    }

    const instance = dictionaryDetails.instance;
    const summary = dictionaryDetails.summary || {};
    const schemas = dictionaryDetails.schemas || [];
    
    // Extract display objects from schemas (tables, views, stored procedures, functions)
    const allObjects = getDisplayObjectsFromSchemas(schemas);
    
    // Dynamically group objects by their type
    const objectsByType = allObjects.reduce((acc, obj) => {
      const type = obj.tableType || obj.objectType || 'OTHER';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(obj);
      return acc;
    }, {});

    const totalItems = allObjects.length;

    // #619: Documentation status is the % of business-context fields filled — one
    // field per object plus one per column — not the % of objects documented.
    const { total: totalContextFields, filled: filledContextFields } = countBusinessContextFields(allObjects);
    const completionPercentage = totalContextFields > 0
      ? Math.round((filledContextFields / totalContextFields) * 100)
      : 0;
    const computedTotalObjects = getComputedTotalObjects(schemas);
    const visibleSummaryKeys = getVisibleSummaryKeys(summary);

    // Build a map of pending changes keyed by object_name for quick lookup
    const pendingChangesByObject = {};
    (pendingChanges || []).forEach((cr) => {
      if (cr.object_name) {
        if (!pendingChangesByObject[cr.object_name]) pendingChangesByObject[cr.object_name] = [];
        pendingChangesByObject[cr.object_name].push(cr);
      }
    });

    const pendingCount = (pendingChanges || []).length;
    const instanceId = instance.instanceId || instance.instance_id;

    return (
      <div className="dd-details-view">
        {/* Summary Section - Same as Step 5 */}
        <div className="step5-container">
          <div className="dictionary-info-row">
            <div className="dictionary-info">
              <TextField
                label="Dictionary Name"
                value={this.state.editedName}
                onChange={this.handleNameChange}
                fullWidth
                required
                disabled={!this.isUserAdmin()}
                error={!this.state.editedName || !this.state.editedName.trim()}
                helperText={!this.state.editedName || !this.state.editedName.trim() ? "Dictionary name is required" : ""}
                style={{ marginBottom: 16 }}
                inputProps={{'data-testid': 'dd-name-input'}}
              />

              <TextField
                label="Description (Optional)"
                value={this.state.editedDescription}
                onChange={this.handleDescriptionChange}
                fullWidth
                multiline
                rows={3}
                disabled={!this.isUserAdmin()}
                placeholder="Add a description for this data dictionary..."
                inputProps={{'data-testid': 'dd-description-input'}}
              />

              {/* Pending Changes Alert */}
              {pendingCount > 0 && (
                <Alert 
                  severity="warning" 
                  variant="outlined"
                  style={{ marginTop: 16, fontSize: '12px' }}
                >
                  <div>
                    <strong>{pendingCount} pending change{pendingCount !== 1 ? 's' : ''}</strong> require{pendingCount === 1 ? 's' : ''} review
                  </div>
                  <MuiButton
                    color="inherit"
                    size="small"
                    onClick={() => this.setState({
                      changeDialogOpen: true,
                      selectedChangeRequest: pendingChanges[0],
                      filteredPendingChanges: pendingChanges
                    })}
                    style={{ marginTop: 8, padding: '4px 8px' }}
                    data-testid="dd-view-changes"
                  >
                    View Changes
                  </MuiButton>
                </Alert>
              )}

              {/* Refresh Schema Button & Import Missing Button.
                  Both diff against the live source. MySQL and MS SQL Server are
                  supported; liveDiffUnsupportedReason is the seam for the next
                  engine. MSSQL shows a caveat below rather than being disabled. */}
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Tooltip title={liveDiffUnsupportedReason(instance) || 'Compare stored schema against live database and detect changes'}>
                  <span>
                    <Button
                      label={isRefreshing ? 'Refreshing...' : 'Refresh Schema'}
                      className="btn btn-default"
                      onClick={() => {
                        if (liveDiffUnsupportedReason(instance)) return;
                        if (onRefreshSchema) onRefreshSchema(instanceId);
                      }}
                      disabled={isRefreshing || !!liveDiffUnsupportedReason(instance)}
                      data-testid="dd-refresh"
                    />
                  </span>
                </Tooltip>
                {this.isUserAdmin() && (
                  <Tooltip title={liveDiffUnsupportedReason(instance) || 'Import objects or backfill attributes that exist in the source database but were skipped during initial import.'}>
                    <span>
                      <Button
                        label="Import Missing Objects/Attributes"
                        className="btn btn-default"
                        onClick={() => {
                          if (liveDiffUnsupportedReason(instance)) return;
                          if (onLoadMissingObjects) onLoadMissingObjects(instanceId);
                          this.setState({ importMissingDialogOpen: true });
                        }}
                        disabled={isLoadingMissing || isRefreshing || !!liveDiffUnsupportedReason(instance)}
                        data-testid="dd-import-missing-open"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
              {liveDiffCaveat(instance) && (
                <Alert severity="info" style={{ marginTop: 12 }} data-testid="dd-mssql-live-diff-notice">
                  {liveDiffCaveat(instance)}
                </Alert>
              )}
            </div>

            <div className="documentation-status-subtle">
              <div className="status-label">Documentation Status</div>
              <div className="status-value">{completionPercentage}%</div>
            </div>
          </div>
        </div>

        {/* Change Detail Sheet */}
        <ChangeRequestDetailSheet
          open={changeDialogOpen}
          onClose={() => this.setState({ changeDialogOpen: false, selectedChangeRequest: null, filteredPendingChanges: [] })}
          changeRequest={selectedChangeRequest}
          changes={this.state.filteredPendingChanges.length > 0 ? this.state.filteredPendingChanges : pendingChanges}
          isLoading={isLoading}
          onNavigate={(direction) => {
            const changesToUse = this.state.filteredPendingChanges.length > 0 ? this.state.filteredPendingChanges : pendingChanges;
            const currentIndex = changesToUse.findIndex(c => c.request_id === selectedChangeRequest.request_id);
            if (direction === 'prev' && currentIndex > 0) {
              this.setState({ selectedChangeRequest: changesToUse[currentIndex - 1] });
            } else if (direction === 'next' && currentIndex < changesToUse.length - 1) {
              this.setState({ selectedChangeRequest: changesToUse[currentIndex + 1] });
            }
          }}
          onAction={(action, comments) => {
            if (onWorkflowAction && selectedChangeRequest) {
              onWorkflowAction(selectedChangeRequest.request_id, action, comments);
            }
            this.setState({ changeDialogOpen: false, selectedChangeRequest: null, filteredPendingChanges: [] });
          }}
        />

        {/* Rollback Confirmation Dialog */}
        {this.renderRollbackConfirmDialog()}

        {/* Import Missing Objects / Attributes Dialog */}
        <ImportMissingDialog
          open={importMissingDialogOpen}
          onClose={() => this.setState({ importMissingDialogOpen: false })}
          missingItems={missingItems}
          isLoadingMissing={isLoadingMissing}
          isImportingMissing={isImportingMissing}
          onImport={(payload) => {
            this.setState({
              importMissingDialogOpen: false,
              pendingImportPayload: payload,
            });
          }}
        />

        {/* Separate Summary Card */}
        <div className="summary-section" style={{ marginTop: 24 }}>
          <h3>Summary &amp; What&apos;s Included</h3>
          
          <div className="summary-stats-grid">
            {schemas.length > 0 && schemas[0].schemaName && (
              <div className="summary-stat-card">
                <div className="stat-label">Schema Name</div>
                <div className="stat-value">{schemas[0].schemaName}</div>
              </div>
            )}

            {/* Dynamically render all properties from the summary object */}
            {visibleSummaryKeys.map((key) => {
              const value = summary[key];
              
              // Skip null, undefined, or empty values
              if (value === null || value === undefined || value === '') return null;
              
              // Format the key from camelCase to readable text
              const formattedKey = key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim();
              
              // Format the value
              let displayValue = key === 'totalObjects' ? computedTotalObjects : value;
              if (typeof value === 'object' && !Array.isArray(value)) {
                displayValue = JSON.stringify(value);
              } else if (Array.isArray(value)) {
                displayValue = value.join(', ');
              } else if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
              }
              
              return (
                <div key={key} className="summary-stat-card">
                  <div className="stat-label">{formattedKey}</div>
                  <div className="stat-value">{String(displayValue)}</div>
                </div>
              );
            })}
            
            {/* Fallback: If summary is empty or doesn't exist, show calculated values */}
            {visibleSummaryKeys.length === 0 && (
              <>
                <div className="summary-stat-card">
                  <div className="stat-label">Type</div>
                  <div className="stat-value">{instance.instanceType || 'N/A'}</div>
                </div>

                <div className="summary-stat-card">
                  <div className="stat-label">Host</div>
                  <div className="stat-value">{instance.host || 'N/A'}:{instance.port || ''}</div>
                </div>

                <div className="summary-stat-card">
                  <div className="stat-label">Environment</div>
                  <div className="stat-value">{instance.environment || 'N/A'}</div>
                </div>

                <div className="summary-stat-card">
                  <div className="stat-label">Total Items</div>
                  <div className="stat-value">{totalItems}</div>
                </div>

                {/* Dynamically show counts for each object type */}
                {Object.keys(objectsByType).sort().map((type) => {
                  const count = objectsByType[type].length;
                  if (count === 0) return null;
                  
                  const typeLabel = this.getTypeLabel(type);
                  return (
                    <div key={type} className="summary-stat-card">
                      <div className="stat-label">{typeLabel}</div>
                      <div className="stat-value">{count}</div>
                    </div>
                  );
                })}

                <div className="summary-stat-card">
                  <div className="stat-label">Documented</div>
                  <div className="stat-value">{filledContextFields} of {totalContextFields} fields</div>
                </div>
              </>
            )}
            
            {/* Always show Documented card if summary exists */}
            {visibleSummaryKeys.length > 0 && (
              <div className="summary-stat-card">
                <div className="stat-label">Documented</div>
                <div className="stat-value">{filledContextFields} of {totalContextFields} fields</div>
              </div>
            )}
          </div>
        </div>

        {/* Schema-level access: who owns it, and who may see it (slice 5o-b).
            Classification lives in this card rather than a new one because the card already carries
            the framing — the ownership label below says "controls visibility & delegation", and
            classification is the other half of that same sentence. */}
        {schemas.filter((s) => s.schemaId || s.schema_id).length > 0 && (
          <div className="rounded-card" style={{ marginTop: 24, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Schema Access</h3>
            {schemas.filter((s) => s.schemaId || s.schema_id).map((s) => (
              <div key={s.schemaId || s.schema_id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.schemaName || s.schema_name}</div>
                <OwnershipPanel entityType="schema" entityId={s.schemaId || s.schema_id} />
                {/* Braces without inner spaces, unlike this file's prevailing style: that style
                    trips object-curly-spacing 248 times here already, and this slice holds lint at
                    parity rather than adding to it. */}
                <div className="panel-section" style={{marginTop: 12}}>
                  <label className="panel-label">
                    Classification <span style={{color: '#9e9e9e', fontWeight: 400}}>(who can see this schema and everything in it)</span>
                  </label>
                  <ClassificationPanel
                    entityType="SCHEMA"
                    entityId={s.schemaId || s.schema_id}
                    classification={s.classification}
                    label={s.schemaName || s.schema_name}
                  />
                </div>
                {/* Mockup screen S4's table. ADDITIVE — the card grid above stays untouched;
                    porting the mockup's table over it would duplicate DDItemCard's data source
                    and create a second edit surface for the same tables. */}
                <ObjectVisibilityTable
                  schemaId={s.schemaId || s.schema_id}
                  onClassify={(row) => this.openDrawerForObject(row)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Processed Changes Section — Rollback Available */}
        {(() => {
          const processedChanges = (pendingChanges || []).filter(cr => this.canRollback(cr.status));
          if (processedChanges.length === 0) return null;
          return (
            <div className="rounded-card" style={{ marginTop: 24, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span className="material-icons" style={{ color: '#e65100' }}>undo</span>
                <h3 style={{ margin: 0 }}>Processed Changes — Rollback Available</h3>
                <Chip
                  label={processedChanges.length}
                  size="small"
                  style={{ backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }}
                />
              </div>
              <Typography variant="body2" style={{ color: '#757575', marginBottom: 16 }}>
                The following change requests have been Approved, Rejected, or Ignored. You can roll back any of these actions if they were made in error.
              </Typography>
              <Table size="small" className="data-table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Object</strong></TableCell>
                    <TableCell><strong>Column</strong></TableCell>
                    <TableCell><strong>Change Type</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Reviewed By</strong></TableCell>
                    <TableCell><strong>Reviewed At</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedChanges.map((cr) => (
                    <TableRow key={cr.request_id} hover data-testid="dd-processed-change-row" data-request-id={cr.request_id}>
                      <TableCell>{cr.object_name || '—'}</TableCell>
                      <TableCell>{cr.column_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={cr.change_type}
                          size="small"
                          style={{
                            backgroundColor: cr.change_type === 'CREATE' ? '#e8f5e9' : cr.change_type === 'DELETE' ? '#ffebee' : '#fff8e1',
                            color: cr.change_type === 'CREATE' ? '#2e7d32' : cr.change_type === 'DELETE' ? '#c62828' : '#f57c00',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cr.status}
                          size="small"
                          style={this.getStatusChipStyle(cr.status)}
                        />
                      </TableCell>
                      <TableCell>{cr.reviewed_by || '—'}</TableCell>
                      <TableCell>
                        {cr.reviewed_at ? new Date(cr.reviewed_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Roll back this ${cr.status.toLowerCase()} action`}>
                          <MuiButton
                            size="small"
                            variant="outlined"
                            onClick={() => this.openRollbackDialog(cr)}
                            style={{ borderColor: '#e65100', color: '#e65100', textTransform: 'none' }}
                            startIcon={<span className="material-icons" style={{ fontSize: 16 }}>undo</span>}
                            data-testid="dd-rollback-open"
                          >
                            Rollback
                          </MuiButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })()}

        {/* Details Section with Step 4 Style Accordions */}
        <div className="step4-container-with-panel">
          <div className="step4-main-content">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Dictionary Contents</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {!this.isUserAdmin() && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={this.state.filterOwnedByMe}
                        onChange={this.handleFilterOwnedByMe}
                        color="primary"
                        inputProps={{'data-testid': 'dd-owned-by-me-toggle'}}
                      />
                    }
                    label="Owned by Me"
                  />
                )}
                <TextField
                  placeholder="Search dictionary contents..."
                  value={this.state.searchQuery}
                  onChange={this.handleSearchChange}
                  style={{ width: 300 }}
                  InputProps={{
                    startAdornment: (
                      <span className="material-icons" style={{ fontSize: 20, marginRight: 8, color: '#757575' }}>
                        search
                      </span>
                    )
                  }}
                  inputProps={{'data-testid': 'dd-search-input'}}
                />
              </div>
            </div>


            {/* Grouped Card Grid - Step 4 Style */}
            <div className="items-categories-container">
              {Object.keys(objectsByType).sort().map((type) => {
                const objects = objectsByType[type];
                const panelKey = this.getTypePanelKey(type);
                const typeLabel = this.getCategoryLabel(type);
                const isCategoryExpanded = this.state.expandedCategories[panelKey] !== false; // Default to true

                // Filter objects based on search query
                const filteredObjects = objects.filter(obj => {
                  // Apply search filter
                  if (this.state.searchQuery) {
                    const query = this.state.searchQuery.toLowerCase();
                    const name = (obj.tableName || obj.name || '').toLowerCase();
                    const context = (obj.businessContext || '').toLowerCase();
                    if (!name.includes(query) && !context.includes(query)) {
                      return false;
                    }
                  }
                  
                  // Apply owned by me filter
                  if (this.state.filterOwnedByMe) {
                    return this.isOwnedByMe(obj);
                  }
                  
                  return true;
                });

                // Derive pending objects for this section from pendingImportPayload
                const pendingObjects = this.state.pendingImportPayload
                  ? (this.state.pendingImportPayload.objects || []).filter((obj) => {
                      const objType = (obj.objectType || obj.type || '').toUpperCase();
                      const sectionTypeUpper = type.toUpperCase();
                      if (sectionTypeUpper === 'BASE TABLE' || sectionTypeUpper === 'TABLE') {
                        return objType === 'TABLE' || objType === 'BASE TABLE';
                      }
                      return objType === sectionTypeUpper;
                    })
                  : [];

                // Don't render category if no matching objects and no pending objects
                if (filteredObjects.length === 0 && pendingObjects.length === 0) return null;

                // Count pending change *requests* (not distinct objects) so the
                // badge reconciles with the header — see countCategoryPendingChanges.
                const categoryPendingChanges = countCategoryPendingChanges(objects, pendingChangesByObject);

                return (
                  <div key={type} className="schema-group">
                    <div
                      className="schema-group-header"
                      onClick={() => this.toggleCategory(panelKey)}
                      data-testid="dd-category-header"
                      data-category={type}
                    >
                      <span className="material-icons">
                        {isCategoryExpanded ? 'expand_more' : 'chevron_right'}
                      </span>
                      <h4>{typeLabel} ({filteredObjects.length}{this.state.searchQuery ? ` of ${objects.length}` : ''})</h4>
                      {pendingObjects.length > 0 && (
                        <span className="pending-section-badge">
                          <span className="material-icons">schedule</span>
                          {pendingObjects.length} pending import{pendingObjects.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    
                    {isCategoryExpanded && (
                      <>
                        {categoryPendingChanges > 0 && (
                          <Alert 
                            severity="warning" 
                            variant="outlined"
                            style={{ margin: '12px 16px', fontSize: '12px' }}
                          >
                            <div>
                              {categoryPendingChanges} pending change{categoryPendingChanges !== 1 ? 's' : ''} in this category
                            </div>
                            <MuiButton 
                              color="inherit" 
                              size="small"
                              onClick={() => {
                                // Filter pending changes to only show items in this category
                                const objectNamesInCategory = filteredObjects.map(o => o.tableName || o.name || o.objectName);
                                const categoryChanges = (pendingChanges || []).filter(pc => 
                                  objectNamesInCategory.includes(pc.object_name)
                                );
                                const firstChange = categoryChanges[0];
                                if (firstChange) {
                                  this.setState({ 
                                    changeDialogOpen: true, 
                                    selectedChangeRequest: firstChange,
                                    filteredPendingChanges: categoryChanges 
                                  });
                                }
                              }}
                              style={{ marginTop: 8, padding: '4px 8px' }}
                            >
                              View Changes
                            </MuiButton>
                          </Alert>
                        )}
                        <div className="schema-items">
                          {filteredObjects.map((obj, index) => this.renderItemCard(obj, type, index))}
                          {pendingObjects.map((obj) => this.renderPendingTile(obj, typeLabel))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Render new sections for pending types that don't exist yet */}
              {this.state.pendingImportPayload && (() => {
                const existingTypes = new Set(Object.keys(objectsByType).map(t => t.toUpperCase()));
                const allPendingObjects = this.state.pendingImportPayload.objects || [];
                const pendingByNewType = {};
                allPendingObjects.forEach((obj) => {
                  const objType = (obj.objectType || obj.type || 'OTHER').toUpperCase();
                  const isNew = !Array.from(existingTypes).some(et => {
                    if (et === 'BASE TABLE' || et === 'TABLE') return objType === 'TABLE' || objType === 'BASE TABLE';
                    return et === objType;
                  });
                  if (isNew) {
                    if (!pendingByNewType[objType]) pendingByNewType[objType] = [];
                    pendingByNewType[objType].push(obj);
                  }
                });
                return Object.keys(pendingByNewType).map((objType) => {
                  const typeLabel = this.getCategoryLabel(objType);
                  const panelKey = this.getTypePanelKey(objType);
                  const isCategoryExpanded = this.state.expandedCategories[panelKey] !== false;
                  return (
                    <div key={`pending-section-${objType}`} className="schema-group">
                      <div className="schema-group-header" onClick={() => this.toggleCategory(panelKey)}>
                        <span className="material-icons">
                          {isCategoryExpanded ? 'expand_more' : 'chevron_right'}
                        </span>
                        <h4>{typeLabel} (0)</h4>
                        <span className="pending-section-badge">
                          <span className="material-icons">schedule</span>
                          {pendingByNewType[objType].length} pending import{pendingByNewType[objType].length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {isCategoryExpanded && (
                        <div className="schema-items">
                          {pendingByNewType[objType].map((obj) => this.renderPendingTile(obj, typeLabel))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Side Panel - Step 4 Style */}
          {this.state.selectedItem && (
            <div className="details-panel-container">
              <div className="details-panel-header">
                <div>
                  <h3>{this.state.selectedItem.tableName || this.state.selectedItem.name || 'Not Available'}</h3>
                  <div className="panel-item-type">
                    {this.getCategoryLabel(this.state.selectedItem.tableType || this.state.selectedItem.objectType || this.state.selectedItemType)}
                  </div>
                </div>
                <IconButton size="small" onClick={this.closeDrawer}>
                  <span className="material-icons">close</span>
                </IconButton>
              </div>
              
              <div className="details-panel-content">
                {/* Item Level Context */}
                <div className="panel-section">
                  <label className="panel-label">Business Context</label>
                  <TextField
                    value={this.state.editedBusinessContext}
                    onChange={this.handleBusinessContextChange}
                    multiline
                    rows={4}
                    fullWidth
                    disabled={!this.canEditItem(this.state.selectedItem)}
                    placeholder={this.canEditItem(this.state.selectedItem) ? "Enter business context for this item..." : "View only - You must be an owner of this item to edit"}
                    helperText={this.canEditItem(this.state.selectedItem) ? `${(this.state.editedBusinessContext || '').length}/500 characters` : "Read-only access"}
                    inputProps={{ maxLength: 500, 'data-testid': 'dd-context-input' }}
                    variant="outlined"
                  />
                </div>

                {/* Classification (slice 5o-b). State plus an action, deliberately unlike every
                    other field in this drawer: the rest are batched by handleSaveAll into one direct
                    write, and this one decides who may SEE the row, so it goes through Change
                    Control. Looking different from its neighbours is the point — a user who edits it
                    and presses Save Changes must not believe the object is now confidential. */}
                <div className="panel-section">
                  <label className="panel-label">Classification</label>
                  <ClassificationPanel
                    entityType="OBJECT"
                    entityId={this.state.selectedItemObjectId}
                    classification={this.state.selectedItem.classification}
                    label={this.state.selectedItem.tableName || this.state.selectedItem.name}
                    showSaveNote
                  />
                </div>

                {/* Mockup screen S4's "who can see it". Immediately after Classification, because
                    it is the consequence of the field above it — and a same-shape .panel-section
                    insertion, not a layout change. */}
                <div className="panel-section">
                  <label className="panel-label">Who can see it</label>
                  <WhoCanSeePanel entityId={this.state.selectedItemObjectId} />
                </div>

                {/* Business Owner Section */}
                <div className="panel-section">
                  <label className="panel-label">Business Owner</label>
                  {this.isUserAdmin() || this.isUserTechnical() ? (
                    <Autocomplete
                      multiple
                      value={this.state.ownerValue}
                      onChange={this.handleOwnerChange}
                      onOpen={this.handleOwnerDropdownOpen}
                      options={this.getOwnerOptions()}
                      getOptionLabel={(option) => option.label}
                      getOptionSelected={(option, value) => option && value && String(option.value) === String(value.value)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.label}
                            {...getTagProps({ index })}
                            key={option.value}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={this.state.ownerValue.length === 0 ? "Not assigned" : "Select owners..."}
                          inputProps={{...params.inputProps, 'data-testid': 'dd-owner-business-input'}}
                        />
                      )}
                    />
                  ) : (
                    <div style={{ padding: '8px 0', color: '#424242', fontSize: '14px' }}>
                      {this.state.selectedItem && this.state.selectedItem.businessOwner ? (
                        Array.isArray(this.state.selectedItem.businessOwner) ? (
                          this.state.selectedItem.businessOwner.length > 0 ? (
                            this.state.selectedItem.businessOwner.map((owner, idx) => (
                              <div key={idx} style={{ marginBottom: idx < this.state.selectedItem.businessOwner.length - 1 ? '4px' : '0' }}>
                                {typeof owner === 'object' ? `${owner.name}${owner.email ? ` (${owner.email})` : ''}` : owner}
                              </div>
                            ))
                          ) : 'Not assigned'
                        ) : typeof this.state.selectedItem.businessOwner === 'object' ? (
                          <span>{this.state.selectedItem.businessOwner.name}{this.state.selectedItem.businessOwner.email && ` (${this.state.selectedItem.businessOwner.email})`}</span>
                        ) : (
                          <span>{this.state.selectedItem.businessOwner}</span>
                        )
                      ) : (
                        <span>Not assigned</span>
                      )}
                      {/* #594: owners are reassignable by admins/technical owners only —
                          say so, instead of leaving a silent read-only field. */}
                      <p className="panel-readonly-note" style={{ marginTop: 6, marginBottom: 0, fontSize: 12, fontStyle: 'italic', color: '#9e9e9e' }}>
                        Only administrators and technical owners can change owners.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tech Owner Section */}
                <div className="panel-section">
                  <label className="panel-label">Tech Owner</label>
                  {this.isUserAdmin() || this.isUserTechnical() ? (
                    <Autocomplete
                      multiple
                      value={this.state.techOwnerValue}
                      onChange={this.handleTechOwnerChange}
                      onOpen={this.handleTechOwnerDropdownOpen}
                      options={this.getTechOwnerOptions()}
                      getOptionLabel={(option) => option.label}
                      getOptionSelected={(option, value) => option && value && String(option.value) === String(value.value)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.label}
                            {...getTagProps({ index })}
                            key={option.value}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={this.state.techOwnerValue.length === 0 ? "Not assigned" : "Select tech owners..."}
                          inputProps={{...params.inputProps, 'data-testid': 'dd-owner-tech-input'}}
                        />
                      )}
                    />
                  ) : (
                    <div style={{ padding: '8px 0', color: '#424242', fontSize: '14px' }}>
                      {this.state.selectedItem && this.state.selectedItem.techOwner ? (
                        Array.isArray(this.state.selectedItem.techOwner) ? (
                          this.state.selectedItem.techOwner.length > 0 ? (
                            this.state.selectedItem.techOwner.map((owner, idx) => (
                              <div key={idx} style={{ marginBottom: idx < this.state.selectedItem.techOwner.length - 1 ? '4px' : '0' }}>
                                {typeof owner === 'object' ? `${owner.name}${owner.email ? ` (${owner.email})` : ''}` : owner}
                              </div>
                            ))
                          ) : 'Not assigned'
                        ) : typeof this.state.selectedItem.techOwner === 'object' ? (
                          <span>{this.state.selectedItem.techOwner.name}{this.state.selectedItem.techOwner.email && ` (${this.state.selectedItem.techOwner.email})`}</span>
                        ) : (
                          <span>{this.state.selectedItem.techOwner}</span>
                        )
                      ) : (
                        <span>Not assigned</span>
                      )}
                      {/* #594: owners are reassignable by admins/technical owners only —
                          say so, instead of leaving a silent read-only field. */}
                      <p className="panel-readonly-note" style={{ marginTop: 6, marginBottom: 0, fontSize: 12, fontStyle: 'italic', color: '#9e9e9e' }}>
                        Only administrators and technical owners can change owners.
                      </p>
                    </div>
                  )}
                </div>

                {/* Columns Section */}
                {this.state.selectedItem.columns && this.state.selectedItem.columns.length > 0 && (
                  <div className="panel-columns-section">
                    <div className="panel-section-header">
                      <h4 className="panel-section-title">
                        Columns ({this.state.selectedItem.columns.length})
                      </h4>
                      <span
                        className="panel-expand-toggle"
                        onClick={() => {
                          const columns = this.state.selectedItem.columns;
                          const expandedCount = Object.keys(this.state.expandedColumnsInPanel).filter(key => this.state.expandedColumnsInPanel[key]).length;
                          if (expandedCount === columns.length) {
                            this.setState({ expandedColumnsInPanel: {} });
                          } else {
                            const allExpanded = {};
                            columns.forEach(col => allExpanded[col.columnId] = true);
                            this.setState({ expandedColumnsInPanel: allExpanded });
                          }
                        }}
                      >
                        {(() => {
                          const columns = this.state.selectedItem.columns;
                          const expandedCount = Object.keys(this.state.expandedColumnsInPanel).filter(key => this.state.expandedColumnsInPanel[key]).length;
                          return expandedCount === columns.length ? "Collapse All" : "Expand All";
                        })()}
                      </span>
                    </div>
                    
                    {this.state.selectedItem.columns.map((col, index) => {
                      const isExpanded = this.state.expandedColumnsInPanel[col.columnId];
                      
                      return (
                        <div
                          key={col.columnId || index}
                          className={`panel-column-item ${isExpanded ? 'expanded' : ''}`}
                          data-testid="dd-column-row"
                          data-column={col.name}
                        >
                          <div
                            onClick={() => this.toggleColumnInPanel(col.columnId)}
                            className={`panel-column-header ${isExpanded ? 'expanded' : ''}`}
                          >
                            <span className="material-icons">
                              {isExpanded ? 'expand_more' : 'chevron_right'}
                            </span>
                            {this.getStatusIcon(col)}
                            <span className="panel-column-name">
                              {col.name}
                            </span>
                            {col.isPii && (
                              <span className="panel-pii-badge">PII</span>
                            )}
                            {col.isAiEligible && (
                              <span className="panel-ai-eligible-badge">AI Sensitive</span>
                            )}
                            <span className="panel-column-type">
                              {col.dataType}
                            </span>
                          </div>
                          
                          {isExpanded && (
                            <div className="panel-column-content">
                              <label className="panel-label-small">
                                Column Business Context
                              </label>
                              <TextField
                                value={col.businessContext || ''}
                                onChange={(e) => {
                                  // Update column context in place
                                  const value = e.target.value;
                                  const updatedColumns = [...this.state.selectedItem.columns];
                                  updatedColumns[index] = { ...col, businessContext: value };
                                  this.setState({
                                    selectedItem: {
                                      ...this.state.selectedItem,
                                      columns: updatedColumns
                                    },
                                    hasUnsavedChanges: true
                                  });
                                  // Propagate to the page so the unsaved-changes guard sees it.
                                  if (col.columnId && this.props.onUpdateColumnBusinessContext) {
                                    this.props.onUpdateColumnBusinessContext(col.columnId, value);
                                  }
                                }}
                                multiline
                                rows={3}
                                fullWidth
                                size="small"
                                disabled={!this.canEditItem(this.state.selectedItem)}
                                placeholder={this.canEditItem(this.state.selectedItem) ? "Describe this column's business purpose..." : "View only"}
                                helperText={this.canEditItem(this.state.selectedItem) ? `${(col.businessContext || '').length}/500` : "Read-only access"}
                                inputProps={{ maxLength: 500, 'data-testid': 'dd-column-context-input' }}
                                variant="outlined"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={!!col.isPii}
                                    onChange={() => this.handlePiiToggle(col, index)}
                                    color="secondary"
                                    disabled={!this.canEditItem(this.state.selectedItem)}
                                    inputProps={{'data-testid': 'dd-pii-toggle'}}
                                  />
                                }
                                label="PII"
                                style={{ marginTop: 8 }}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={!!col.isAiEligible}
                                    onChange={() => this.handleAiEligibleToggle(col, index)}
                                    color="primary"
                                    disabled={!this.canEditItem(this.state.selectedItem)}
                                    inputProps={{'data-testid': 'dd-ai-sensitive-toggle'}}
                                  />
                                }
                                label="AI Sensitive"
                                style={{ marginTop: 8 }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="details-panel-footer" style={{ justifyContent: 'flex-end' }}>
                <Button
                  label="Close"
                  className="btn btn-default"
                  onClick={this.closeDrawer}
                  data-testid="dd-panel-close"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons at Bottom */}
        <div className="dd-actions-container" style={{
          marginTop: 32,
          paddingTop: 24,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 16,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}>
          {pendingImportPayload && (
            <button
              className="cancel-pending-import-btn"
              onClick={() => this.setState({ pendingImportPayload: null })}
            >
              <span className="material-icons">cancel</span>
              Cancel pending import ({(pendingImportPayload.objects || []).length} object{(pendingImportPayload.objects || []).length !== 1 ? 's' : ''})
            </button>
          )}
          <Button
            label="Cancel"
            className="btn btn-default"
            onClick={this.handleCancel}
            data-testid="dd-cancel"
          />
          <Button
            label="Save Changes"
            className="btn btn-action"
            onClick={this.handleSaveAll}
            disabled={!(this.state.hasUnsavedChanges || pendingImportPayload)}
            data-testid="dd-save"
          />
        </div>
      </div>
    );
  }
}

export default DDDetailsView;
